import { BodyText, mq, Spacer, TinyTitleBold } from '@kvdbil/components';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { closeBiddingView } from '~/App/shared/actions/objectViewDrawerActions';
import ObjectSummaryRow from '~/App/shared/components/BiddingSummary';
import BiddingInformation from '~/App/shared/components/BiddingSummary/BiddingInformation';
import Guide from '~/App/shared/components/Guide/Guide';
import { AuctionType } from '~/App/shared/interfaces/Auction';
import { ProcessedAuction } from '~/App/shared/interfaces/ProcessedAuction';
import { objectViewDrawerBuyMethod } from '~/App/shared/selectors/storeObject';
import { getAuctionPreviewImageAltText } from '~/helpers/auction';
import { formatMoney } from '~/helpers/currency';
import { useTranslation } from '~/Locale';

const TopRow = styled.div<{ isBiddingGuideIsOpen: boolean }>`
  position: relative;
  max-width: 1024px;
  margin: auto;

  ${({ isBiddingGuideIsOpen }) => {
    if (isBiddingGuideIsOpen) {
      return css`
        ${mq('tablet')} {
          height: 15rem;
        }
        ${mq(null, 'tablet')} {
          height: 22rem;
        }
      `;
    }
  }}
`;

const FixedPriceInfo = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 3rem;

  ${mq(null, 'tablet')} {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const FixedPriceInfoItem = styled(FixedPriceInfo)`
  gap: 1rem;
  flex-direction: row;
  ${mq(null, 'tablet')} {
    justify-content: space-between;
  }
`;

// High number to make sure it always goes beyond parent and fills full width
const negativePosition = '-5rem';

export const BiddingViewSeparator = styled.div`
  position: absolute;
  bottom: 0;
  left: ${negativePosition};
  right: ${negativePosition};
  height: 1px;
  background-color: ${({ theme }) => theme.colors.grayscaleToned.light5};
`;

interface Props {
  auction: ProcessedAuction;
  previewImage: string;
  isClosed: boolean;
}

const BiddingViewTopRow = ({ auction, previewImage, isClosed }: Props) => {
  const { t } = useTranslation();
  const [isBiddingGuideIsOpen, setIsBiddingGuideIsOpen] = useState(false);

  const buyMethod = useSelector(objectViewDrawerBuyMethod);

  const dispatch = useDispatch();

  const handleCloseBiddingView = () => {
    dispatch(closeBiddingView());
  };

  const toggleBiddingGuide = () => {
    setIsBiddingGuideIsOpen(prevstate => !prevstate);
  };

  const fixedPriceValue = formatMoney({
    value: auction.buyNowAmount ?? 0,
    currency: t('SEK')
  });

  return (
    <TopRow isBiddingGuideIsOpen={isBiddingGuideIsOpen}>
      {buyMethod === AuctionType.BIDDING &&
        (isBiddingGuideIsOpen ? (
          <Guide handleClose={toggleBiddingGuide} />
        ) : (
          <ObjectSummaryRow
            auction={auction}
            imageAltText={getAuctionPreviewImageAltText(
              t,
              auction.processObject
            )}
            image={previewImage}
            auctionIsClosed={isClosed}
            closeBiddingView={handleCloseBiddingView}
            isBiddingGuideOpen={isBiddingGuideIsOpen}
          >
            <Spacer height={2} />
            <BiddingInformation
              auction={auction}
              handleGuideClick={toggleBiddingGuide}
            />
          </ObjectSummaryRow>
        ))}

      {buyMethod === AuctionType.BUY_NOW && (
        <ObjectSummaryRow
          auction={auction}
          imageAltText={getAuctionPreviewImageAltText(t, auction.processObject)}
          image={previewImage}
          auctionIsClosed={isClosed}
          closeBiddingView={handleCloseBiddingView}
          isBiddingGuideOpen={isBiddingGuideIsOpen}
        >
          <Spacer height={1} />
          <FixedPriceInfo>
            <FixedPriceInfoItem>
              <BodyText>{t('Registration number')}</BodyText>
              <TinyTitleBold>
                {auction.processObject.baseObject.registrationPlate}
              </TinyTitleBold>
            </FixedPriceInfoItem>

            <FixedPriceInfoItem>
              <BodyText>{t('Price incl. VAT')}</BodyText>
              <TinyTitleBold>{fixedPriceValue}</TinyTitleBold>
            </FixedPriceInfoItem>
          </FixedPriceInfo>
        </ObjectSummaryRow>
      )}
      <BiddingViewSeparator />
    </TopRow>
  );
};

export default BiddingViewTopRow;
